import { codeUnitString } from "../code-unit-string.js";
import { cpAuditChangesSchema, cpAuditJsonByteLength } from "./changes.js";
import { CP_AUDIT_ACTION_NAMES, CP_AUDIT_ACTOR_KINDS, CP_AUDIT_OUTCOMES, CP_AUDIT_RESOURCE_TYPE_NAMES } from "./vocabulary.js";
import { singleLineText } from "../single-line-text.js";
import { z } from "zod";
//#region src/cp-audit/ingest.ts
/**
* The body of `POST /internal/audit`, the one door through which platform-api, release-engine,
* provisioning-svc and site-admin-api write a row to `cp_auth.audit_log`.
*
* Plan `2026-10-14-cp-audit-coverage`, decisions D-7, D-18 and D-19, defaults rows F-3 and F-9,
* rulings R-003 and R-004.
*
* Who uses it:
*
* - `CpAuditClient` in `@zion/cp-audit-client` builds the body as a {@link CpAuditIngest} and sends
*   it with a service token. Each attempt of one row carries the same
*   {@link CP_AUDIT_IDEMPOTENCY_KEY_HEADER} header (R-003). For one release, the client sends the
*   old body once when a 400 names `actorKind`, `actorLabel` or `ipAddress` as unknown keys (R-004).
* - cp-auth-api binds `auditIngestBodySchema` on the route, under the allowlist of the four service
*   subjects and `serviceOnly: true`. That union takes this schema first and the old body second,
*   for one release (R-004, R-005). It stores one row per pair of `actor_service` and idempotency
*   key, and answers 204 for a repeat (R-003).
*
* What the body never carries:
*
* - The service that sends it. cp-auth-api writes `actor_service` from the subject of the verified
*   token (`svc:<name>`), never from the body, so one service cannot write a row in the name of
*   another.
* - The client address. The schema refuses `ipAddress` (F-3): a caller-supplied address is no
*   evidence, and the in-mesh address is a node address (#1740).
* - A secret value in `details` (F-12). cp-auth-api still runs `redactCpAuditValue` on `details`
*   before the insert, as a backstop. The backstop keeps each entry of `details.changes` in its
*   `{ from, to }` shape, so the stored row still reads back with `readCpAuditChanges`.
*
* A body that breaks a rule answers 400 with the standard envelope of `ZionStrictValidationPipe`.
* The schema has refinements and no transform, so `createOpenApiDocument()` can represent it.
*/
/**
* The request header of `POST /internal/audit` that carries the idempotency key of one row (R-003).
*
* The value is one UUID per row. `CpAuditClient` draws it once for each `record` call and sends it
* on every attempt of that row. cp-auth-api stores at most one row for each pair of `actor_service`
* and key, so a retry after a commit that the client did not see writes no second row. HTTP header
* names compare without case; the text is lower case, the form Node gives in `req.headers`.
*/
const CP_AUDIT_IDEMPOTENCY_KEY_HEADER = "idempotency-key";
/**
* The value rule of {@link CP_AUDIT_IDEMPOTENCY_KEY_HEADER}: one RFC 9562 UUID, in any letter case.
* `CpAuditClient` checks a caller key with it before the first POST, and a test double of the sink
* checks it the same way. Any other value, `null` included, fails the parse.
*/
const cpAuditIdempotencyKeySchema = z.uuid();
/** The largest `details` object, as UTF-8 bytes of its JSON text: 16 KB. */
const CP_AUDIT_DETAILS_MAX_BYTES = 16384;
/**
* The longest `resourceId`. The column `resource_id` keeps a UUID and the column `resource_key`
* (D-19) keeps any other id, such as `'rate-card'` (#3307).
*/
const CP_AUDIT_RESOURCE_ID_MAX_LENGTH = 200;
/** The longest `actorLabel`. */
const CP_AUDIT_ACTOR_LABEL_MAX_LENGTH = 200;
/** The longest `correlationId`, the bound of the ingest before this plan. */
const CP_AUDIT_CORRELATION_ID_MAX_LENGTH = 256;
/** The actor kinds that never name an operator: a program acted, and no person asked. */
const CP_AUDIT_ACTOR_KINDS_WITHOUT_OPERATOR = [
	"service",
	"system",
	"dp_operator"
];
/**
* One line of text: 1 to `max` UTF-16 code units with no control character (\p{Cc}). It is
* `singleLineText` of the neutral module `single-line-text`, the one definition that the audit
* ingest, the read-audit details and the DP log query levels share.
*/
const cpAuditSingleLine = singleLineText;
/**
* The strict body of `POST /internal/audit`.
*
* Fields:
*
* - `action`: a key of `CP_AUDIT_ACTIONS`. Any other string answers 400.
* - `actorKind`: one of `CP_AUDIT_ACTOR_KINDS`.
* - `operatorId`: the operator UUID, or `null`. Required, and not `null`, when `actorKind` is
*   `operator`. Absent or `null` when `actorKind` is `service`, `system` or `dp_operator`. Either is
*   valid for `script`.
* - `actorLabel`: optional display text for the actor, 1 to 200 characters on one line. The DP
*   operator program sends the data plane name; a reconciler sends its class name.
* - `resourceType`: optional, a key of `CP_AUDIT_RESOURCE_TYPES`.
* - `resourceId`: optional, any id text of 1 to 200 characters on one line. Valid only with a
*   `resourceType`.
* - `outcome`: optional, one of `CP_AUDIT_OUTCOMES`.
* - `correlationId`: optional, 1 to 256 characters. cp-auth-api uses the active trace id when it
*   is absent.
* - `details`: optional object, at most 16 KB as JSON text. A `details.changes` key must parse as
*   `cpAuditChangesSchema`, the output of `auditChanges`. That output is at most 12 KB
*   (`CP_AUDIT_CHANGES_MAX_BYTES`), so the other keys of `details` keep 4,084 bytes.
*
* Any other key answers 400 (F-3).
*/
const cpAuditIngestSchema = z.object({
	action: z.enum(CP_AUDIT_ACTION_NAMES).describe("The action, a key of CP_AUDIT_ACTIONS."),
	actorKind: z.enum(CP_AUDIT_ACTOR_KINDS).describe("Who acted: a person, a service, a job, a script or a DP."),
	operatorId: z.uuid().nullish().describe("The operator UUID when a person acted."),
	actorLabel: cpAuditSingleLine(200).optional(),
	resourceType: z.enum(CP_AUDIT_RESOURCE_TYPE_NAMES).optional(),
	resourceId: cpAuditSingleLine(200).optional(),
	outcome: z.enum(CP_AUDIT_OUTCOMES).optional(),
	correlationId: codeUnitString().min(1).max(256).optional(),
	details: z.record(z.string(), z.unknown()).optional()
}).strict().superRefine((body, ctx) => {
	const hasOperator = body.operatorId !== void 0 && body.operatorId !== null;
	if (body.actorKind === "operator" && !hasOperator) ctx.addIssue({
		code: "custom",
		path: ["operatorId"],
		message: "operatorId is required when actorKind is operator"
	});
	if (CP_AUDIT_ACTOR_KINDS_WITHOUT_OPERATOR.includes(body.actorKind) && hasOperator) ctx.addIssue({
		code: "custom",
		path: ["operatorId"],
		message: `operatorId must be absent when actorKind is ${body.actorKind}`
	});
	if (body.resourceId !== void 0 && body.resourceType === void 0) ctx.addIssue({
		code: "custom",
		path: ["resourceType"],
		message: "resourceType is required with resourceId"
	});
	if (body.details === void 0) return;
	if (cpAuditJsonByteLength(body.details) > 16384) ctx.addIssue({
		code: "custom",
		path: ["details"],
		message: `details must be at most ${CP_AUDIT_DETAILS_MAX_BYTES} bytes as JSON`
	});
	if (Object.hasOwn(body.details, "changes") && !cpAuditChangesSchema.safeParse(body.details["changes"]).success) ctx.addIssue({
		code: "custom",
		path: ["details", "changes"],
		message: "details.changes must map each field to { from, to, truncated? }"
	});
});
//#endregion
export { CP_AUDIT_ACTOR_KINDS_WITHOUT_OPERATOR, CP_AUDIT_ACTOR_LABEL_MAX_LENGTH, CP_AUDIT_CORRELATION_ID_MAX_LENGTH, CP_AUDIT_DETAILS_MAX_BYTES, CP_AUDIT_IDEMPOTENCY_KEY_HEADER, CP_AUDIT_RESOURCE_ID_MAX_LENGTH, cpAuditIdempotencyKeySchema, cpAuditIngestSchema, cpAuditSingleLine };

//# sourceMappingURL=ingest.js.map